import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { Anchor, Banner } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { stringHelper } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserPropertyDetailsWebApiHandler_overview } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../hooks/queries/properties';

import {
  emitClickContactSupportFromRestrictedBannerEvent,
  emitViewRestrictedListingBannerEvent,
} from '../../analytics/events/dashboardPage';
import { useAuth } from '../AuthProvider';

import type { ValueOf } from 'type-fest';
import {
  type GetUserPropertyDetailsResponseWebDto,
  type GetUserPropertyOverviewResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { BLOCKED_LISTING_TRIGGER_OBJECTS } from '../../analytics/events/dashboardPage';

const WhiteAnchor = (props: React.ComponentProps<typeof Anchor>) => (
  <Anchor {...props} css={{ fontWeight: 'bold', color: 'white' }} />
);

const RestrictedListingBanner = ({ isFromDashboard = false }: { isFromDashboard?: boolean }) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetailsData } = useGetListingDetails(listingAlias, { enabled: !isFromDashboard });
  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) && isFromDashboard },
  );

  const listing: GetUserPropertyDetailsResponseWebDto | GetUserPropertyOverviewResponseWebDto | undefined =
    listingDetailsData ?? overviewData;

  const updateActiveStatus = listing?.updateActiveStatus;
  const active =
    listing && 'active' in listing ? listing.active : listing && 'isActive' in listing ? listing.isActive : false;
  const isListingRestricted = !updateActiveStatus?.isAllowed && !active;
  const shouldRenderBanner = isFromDashboard
    ? updateActiveStatus?.showRestrictionOnDashboard && isListingRestricted
    : isListingRestricted;
  const triggerLocation = isFromDashboard ? 'zrm_property_overview' : 'posting_path_review';

  useEffect(() => {
    if (shouldRenderBanner && user && listing) {
      emitViewRestrictedListingBannerEvent({
        user,
        currentProperty: listing,
        triggerLocation,
      });
    }
  }, [shouldRenderBanner, user, listing, triggerLocation]);

  const handleUrlClick = (event: React.MouseEvent<HTMLAnchorElement>, url: string, text: string) => {
    const triggerObject = text.toLowerCase().replace(/\s+/g, '_') as ValueOf<typeof BLOCKED_LISTING_TRIGGER_OBJECTS>;

    if (user && listing) {
      emitClickContactSupportFromRestrictedBannerEvent({
        user,
        currentProperty: listing,
        triggerLocation,
        triggerObject,
      });
    }

    if (stringHelper.getLastPieceFromPath(url) === 'security-inquiry') {
      event?.preventDefault();
      try {
        sessionStorage.setItem('zrm_security_inquiry_listing_alias', listingAlias);
      } catch {
        console.warn('Error setting listing alias in storage');
      }
      // Strip /rental-manager base path for Next.js routing
      const nextJsPath = url.replace('/rental-manager', '');
      router.push(nextJsPath as '/security-inquiry');
    }
  };

  return shouldRenderBanner ? (
    <Banner tone="warning" data-eventid="7544" data-eventdescription="View restricted listing banner">
      <PartialMessage
        onUrlClick={handleUrlClick}
        partialMessages={updateActiveStatus?.partialMessages ?? []}
        linkComponent={WhiteAnchor}
        eventId="7545"
      />
    </Banner>
  ) : null;
};

export default RestrictedListingBanner;
